import { useState } from 'react';
import { X, Target, Check } from 'lucide-react';
import { campanasMasterService, ConsolidadoZona } from '../../services/campanasMasterService';
import { useErrorHandler } from '../../hooks/useErrorHandler';
import { formatCurrency } from '../../utils/formatters';
import { DialogShell } from '../ui/DialogShell';
import { reportarErrorInesperado } from '../../utils/reportarErrorInesperado';
import { FormErrorAlert } from '../ui/FormErrorAlert';
import { useAuth } from '../../context/AuthContext';
import { puedeAdministrarMaestra } from '../../utils/permissions';

interface Props {
  readonly isOpen: boolean;
  readonly consolidado: ConsolidadoZona;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
}

type ModoDistribucion = 'igual' | 'manual';

interface Fila {
  readonly campanaId: string;
  readonly objetivo: number;
}

function redistribuirIgual(total: number, count: number): number[] {
  if (count === 0) return [];
  const totalCents = Math.round(total * 100);
  const baseCents = Math.floor(totalCents / count);
  const remainder = totalCents - baseCents * count;
  return Array.from({ length: count }, (_, i) => (baseCents + (i < remainder ? 1 : 0)) / 100);
}

export function EditarObjetivosZonaModal(props: Props) {
  const { usuario } = useAuth();
  if (!props.isOpen) return null;
  if (!puedeAdministrarMaestra(usuario, props.consolidado.campana)
    || !['planificacion', 'en_curso'].includes(props.consolidado.campana.estado)) {
    return <DialogShell isOpen onClose={props.onClose} title="Metas de campaña">
      <p>Las metas corresponden a la junta responsable de esta convocatoria mientras esté abierta.</p>
    </DialogShell>;
  }
  return <EditarObjetivosZonaFormulario key={props.consolidado.campana.id} {...props} />;
}

function EditarObjetivosZonaFormulario({ isOpen, consolidado, onClose, onSuccess }: Props) {
  const { usuario } = useAuth();
  const { error, handleError, clearError } = useErrorHandler();
  const [submitting, setSubmitting] = useState(false);
  const [modo, setModo] = useState<ModoDistribucion>('igual');
  const [totalZona, setTotalZona] = useState(() => String(consolidado.totalObjetivoZona));
  const [filas, setFilas] = useState<Fila[]>(() => consolidado.lumisiales.map(l => ({
    campanaId: l.campanaId, objetivo: l.objetivo,
  })));

  const totalZonaNum = Number.parseFloat(totalZona) || 0;
  const sumaFilas = filas.reduce((s, f) => s + f.objetivo, 0);
  const diferencia = totalZonaNum - sumaFilas;

  let diferenciaColor: string;
  if (Math.abs(diferencia) < 0.01) {
    diferenciaColor = 'text-emerald-600';
  } else if (diferencia > 0) {
    diferenciaColor = 'text-amber-600';
  } else {
    diferenciaColor = 'text-red-600';
  }

  const aplicarDistribucionIgual = (total: number) => {
    const montos = redistribuirIgual(total, filas.length);
    setFilas(prev => prev.map((f, i) => ({ ...f, objetivo: montos[i] ?? 0 })));
  };

  const handleTotalChange = (valor: string) => {
    setTotalZona(valor);
    if (modo === 'igual') {
      aplicarDistribucionIgual(Number.parseFloat(valor) || 0);
    }
  };

  const activarModoIgual = () => {
    setModo('igual');
    aplicarDistribucionIgual(totalZonaNum);
  };

  const actualizarFila = (idx: number, valor: number) => {
    setFilas(prev => prev.map((f, i) => i === idx ? { ...f, objetivo: valor } : f));
  };

  const handleSubmit = async () => {
    clearError();
    if (!puedeAdministrarMaestra(usuario, consolidado.campana)) {
      handleError('Esta convocatoria corresponde a otra junta.'); return;
    }
    if (filas.some(f => !Number.isFinite(f.objetivo) || f.objetivo < 0
      || Math.abs(f.objetivo * 100 - Math.round(f.objetivo * 100)) > 0.000001)) {
      handleError('Los objetivos deben ser válidos, no negativos y tener como máximo dos decimales.');
      return;
    }
    setSubmitting(true);
    try {
      const actual = await campanasMasterService.getById(consolidado.campana.id);
      if (!actual || !puedeAdministrarMaestra(usuario, actual) || !['planificacion', 'en_curso'].includes(actual.estado)) {
        throw new Error('La convocatoria cambió.');
      }
      await campanasMasterService.actualizarObjetivosZona(
        consolidado.campana.id,
        filas.map(f => ({ campanaId: f.campanaId, nuevoObjetivo: f.objetivo }))
      );
      onSuccess();
    } catch (e) {
      handleError('No se pudieron guardar las metas. Tus cambios siguen en el formulario.');
      reportarErrorInesperado(e, 'Error al guardar los objetivos. Intenta nuevamente.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogShell
      isOpen={isOpen}
      onClose={onClose}
      onBeforeClose={() => !submitting}
      size="md"
      header={
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-sky-100 p-2 rounded-lg">
                <Target className="w-6 h-6 text-sky-600" />
              </div>
              <div>
                <h2 id="editar-objetivos-title" className="text-xl font-bold text-slate-800">
                  Editar Metas de Mi Zona
                </h2>
                <p className="text-sm text-slate-500">{consolidado.campana.nombre}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
              aria-label="Cerrar modal"
            >
              <X className="w-6 h-6 text-slate-600" />
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-5">
        <FormErrorAlert error={error} />

        <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <label htmlFor="total-zona" className="block text-sm font-semibold text-slate-700 mb-1">
                Objetivo total de mi zona ($)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
                <input
                  id="total-zona"
                  type="number"
                  min="0"
                  step="0.01"
                  value={totalZona}
                  onChange={e => handleTotalChange(e.target.value)}
                  className="w-full pl-7 pr-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 bg-white text-sm"
                />
              </div>
            </div>
            <div className="flex-1">
              <p className="block text-sm font-semibold text-slate-700 mb-1">Distribución</p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={activarModoIgual}
                  className={`flex-1 px-3 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ${
                    modo === 'igual'
                      ? 'border-sky-500 bg-sky-50 text-sky-700'
                      : 'border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                >
                  Igual automático
                </button>
                <button
                  type="button"
                  onClick={() => setModo('manual')}
                  className={`flex-1 px-3 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ${
                    modo === 'manual'
                      ? 'border-sky-500 bg-sky-50 text-sky-700'
                      : 'border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                >
                  Manual
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 pt-2 border-t border-slate-200">
            <div className="text-center">
              <p className="text-xs text-slate-500 mb-0.5">Objetivo zona</p>
              <p className="text-sm font-semibold text-slate-800">{formatCurrency(totalZonaNum)}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-slate-500 mb-0.5">Suma asignada</p>
              <p className="text-sm font-semibold text-slate-800">{formatCurrency(sumaFilas)}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-slate-500 mb-0.5">Diferencia</p>
              <p className={`text-sm font-semibold ${diferenciaColor}`}>
                {Math.abs(diferencia) < 0.01 ? '✓ Cuadra' : formatCurrency(Math.abs(diferencia))}
              </p>
            </div>
          </div>
        </div>

        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <div
            className="grid bg-slate-50 border-b border-slate-200 py-2 px-3 text-xs font-semibold text-slate-500"
            style={{ gridTemplateColumns: '1fr 130px 110px' }}
          >
            <span>Sede</span>
            <span className="hidden sm:block">Diócesis</span>
            <span className="text-right">Objetivo</span>
          </div>
          <div className="divide-y divide-slate-100">
            {filas.map((fila, idx) => {
              const lum = consolidado.lumisiales[idx];
              return (
                <div
                  key={fila.campanaId}
                  className="grid items-center hover:bg-slate-50 transition-colors py-2.5 px-3"
                  style={{ gridTemplateColumns: '1fr 130px 110px' }}
                >
                  <div className="min-w-0">
                    <p className="font-medium text-slate-800 text-sm truncate">{lum.nombre}</p>
                    {lum.ciudad && <p className="text-xs text-slate-500 truncate">{lum.ciudad}</p>}
                  </div>
                  <span className="text-xs text-slate-500 truncate pr-2 hidden sm:block">{lum.diocesisNombre}</span>
                  <div className="text-right">
                    {modo === 'manual' ? (
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={fila.objetivo || ''}
                        onChange={e => actualizarFila(idx, Number.parseFloat(e.target.value) || 0)}
                        className="w-full px-2 py-1 border border-slate-300 rounded text-right focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 text-sm"
                      />
                    ) : (
                      <span className="text-sm font-medium text-slate-800">{formatCurrency(fila.objetivo)}</span>
                    )}
                  </div>
                </div>
              );
            })}
            {filas.length === 0 && (
              <div className="py-8 text-center text-slate-400 text-sm">
                No hay sedes asignadas en esta zona.
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="-mx-6 -mb-6 mt-4 sticky bottom-0 bg-white border-t border-slate-200 p-4 flex items-center gap-3">
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="px-5 py-2.5 bg-slate-100 text-slate-600 font-semibold rounded-lg hover:bg-slate-200 transition-colors disabled:opacity-50 text-sm"
        >
          Cancelar
        </button>
        <div className="flex-1" />
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting || filas.length === 0}
          className="flex items-center gap-2 px-6 py-2.5 bg-sky-600 text-white font-semibold rounded-lg hover:bg-sky-700 transition-colors disabled:opacity-50 text-sm"
        >
          {submitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              Guardando...
            </>
          ) : (
            <>
              <Check className="w-4 h-4" />
              Guardar cambios
            </>
          )}
        </button>
      </div>
    </DialogShell>
  );
}
